import React, { useEffect, useState } from 'react';
import FormRadioGroup from 'Shared/components/FormComponents/FormRadioGroup';
import { Field } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';

import { required } from 'Shared/utils/formValidations';
import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import { useToggle, useFieldValue } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import { reminderTypeOptions } from '../constants';
import { formatOptionsFromArray } from './utils';

import { getUserById } from 'Shared/selectors/workspace';

//Actions
import { fetchGoals } from 'Shared/actions/Goals.actions';

//Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import WorkspaceField from './PrivacyRadioGroup/WorkspaceField/WorkspaceField';
import ParentGoalField from '../ParentGoalField/ParentGoalField';
import WpAlert from 'Wpds/components/alert/WpAlert';

import './PrivacyAndReminders.scss';

type Props = {
  isEdit: boolean;
  workspaceSlug?: string;
  toggleEdit: Function;
};

export default function PrivacyAndRemindersV2({ isEdit, workspaceSlug, toggleEdit }: Props) {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const assigneeId = useFieldValue('assigneeId');
  const workspaceId = useFieldValue('workspaceId');
  const [showAddUserToWorkspaceAlert, setShowAddUserToWorkspaceAlert] = useState(false);

  const parentId = useFieldValue('parentId');
  const [showParentGoalSelect, toggleShowParentGoalSelect] = useToggle(!!parentId);

  useEffect(() => {
    async function fetchData() {
      await dispatch(fetchGoals({ context: 'team', completed: false }));
    }

    fetchData();
  }, []);

  const workspaces = useSelector(state => state.workspaces);
  const assigneeFirstName = useSelector(getUserById(assigneeId))?.firstName;

  useEffect(() => {
    if (workspaceId) {
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (workspace) {
        setShowAddUserToWorkspaceAlert(!workspace.users.find(u => u.id === assigneeId));
      }
    } else {
      setShowAddUserToWorkspaceAlert(false);
    }
  }, [workspaces, workspaceId]);

  const reminderOptions = formatOptionsFromArray(reminderTypeOptions);
  const privacyOptions = [
    { label: tr('perform.goals.privacy.everyone_in_organization'), value: 'public' },
    { label: tr('perform.goals.privacy.just_workspace_members'), value: 'private' },
  ];

  const labelRequired = tr('perform.goals.form.required');
  const labelClassName = 'goals-privacy-field-label';
  const fieldClassName = 'goals-privacy-field-group';
  return (
    <div>
      {!isEdit && (
        <FieldGroup
          label={tr('perform.goals.privacy.add_parent_goal')}
          labelClassName={labelClassName}
          className={fieldClassName}
        >
          {!showParentGoalSelect && (
            <WpButton variant="tertiary" onClick={toggleShowParentGoalSelect}>
              <WpIcon name="link" /> {tr('perform.goals.privacy.link_to_existing_goal')}
            </WpButton>
          )}
          {showParentGoalSelect && <ParentGoalField />}
        </FieldGroup>
      )}
      <FieldGroup
        label={tr('perform.goals.privacy.select_workspace')}
        labelClassName={labelClassName}
        required
        labelRequired={labelRequired}
        className={fieldClassName}
      >
        <WorkspaceField name="workspaceId" oneOnOneUserId={assigneeId} disabled={isEdit} />
        {showAddUserToWorkspaceAlert && (
          <div className="goal-workspace-alert">
            <WpAlert showIcon={false}>{tr('perform.goals.privacy.user_not_in_workspace', assigneeFirstName)}</WpAlert>
          </div>
        )}
      </FieldGroup>
      <FieldGroup
        label={tr('perform.goals.privacy.who_can_view')}
        labelClassName={labelClassName}
        required
        labelRequired={labelRequired}
        className={fieldClassName}
      >
        <Field
          name="privacy"
          options={privacyOptions}
          component={FormRadioGroup}
          validate={required}
          isEdit={isEdit}
          toggleEdit={toggleEdit}
          workspaceSlug={workspaceSlug}
        />
      </FieldGroup>
      <FieldGroup
        label={tr('perform.goals.privacy.check_in_frequency')}
        labelClassName={labelClassName}
        labelRequired={labelRequired}
        description={tr('perform.goals.privacy.check_in_description')}
        className={fieldClassName}
      >
        <Field name="reminder" options={reminderOptions} component={FormRadioGroup} />
      </FieldGroup>
    </div>
  );
}
